import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * Lo que la «Primera travesía» pide a las pantallas reales mientras dura
 * (plan técnico §1). Sin proveedor, `useTravesia()` devuelve `null` y las
 * pantallas se comportan como siempre.
 *
 * Lo leen:
 * - `MobileNavTabs`: `activa` (barra atenuada y sin navegar),
 *   `pestanaEncendida` y `brujulaForzada`.
 * - `AssignKeypad`: `textoSugerido` (etiqueta «Asignar lo sugerido (X)»),
 *   `onAuto` y `autoPulsa` (botón «Auto»).
 * - `BudgetTable`: `bloquearListo`, `planVacioTexto` y la marca
 *   `data-travesia-foco` en la primera fila (con `activa`).
 */
/** Las pestañas de la barra (las mismas que `Pestana` de `opciones/opciones.ts`). */
export type PestanaTravesia =
  | 'gastos'
  | 'plan'
  | 'inicio'
  | 'cuentas'
  | 'informes';

export type TravesiaValor = {
  /** La travesía está en marcha: la barra se atenúa y no navega. */
  activa: boolean;
  /** Pestaña que se ve encendida (la del paso); `null` = la de la ruta. */
  pestanaEncendida: PestanaTravesia | null;
  /** Abre la brújula aunque nadie la haya tocado (paso 8). */
  brujulaForzada?: boolean;
  /** El botón verde del teclado dice «Asignar lo sugerido (X)». */
  textoSugerido: boolean;
  /** «Auto» late para llamar la atención (Gavi lo propone). */
  autoPulsa: boolean;
  /** «Auto» reparte todo (en vez de su menú) y cierra el teclado. */
  onAuto?: () => Promise<void>;
  /** La píldora «listos para asignar» no abre «Asignar el mes». */
  bloquearListo: boolean;
  /** Texto del Plan sin grupos de gasto («Aquí aparecerán tus categorías»). */
  planVacioTexto?: string;
};

/**
 * Pulso del botón «Auto» (`autoPulsa`). Se reexporta aquí porque los `.ts`
 * necesitan entrada propia en `package.json#imports` y los `.tsx` no.
 */
export { clasePulso as claseAutoPulsa } from './gaviCss';

/** Atributo de la primera fila de categoría del Plan (foco del paso 7). */
export const ATRIBUTO_FOCO = 'data-travesia-foco';

const TravesiaContext = createContext<TravesiaValor | null>(null);

type TravesiaProviderProps = {
  /** `null` fuera de la travesía (el árbol no cambia al entrar o salir). */
  value: TravesiaValor | null;
  children: ReactNode;
};

export function TravesiaProvider({ value, children }: TravesiaProviderProps) {
  return (
    <TravesiaContext.Provider value={value}>
      {children}
    </TravesiaContext.Provider>
  );
}

/** `null` fuera de la travesía: nada cambia. */
export function useTravesia(): TravesiaValor | null {
  return useContext(TravesiaContext);
}
